/**
 * Pull down to refresh.
 *
 * The app already polls every few seconds, so this adds almost nothing to how
 * current the numbers are. It exists because in a standalone PWA there is no
 * browser chrome, and a pull at the top of the page therefore does nothing at
 * all — which is indistinguishable from a frozen app to somebody who has just
 * started doubting the screen. That doubt arrives exactly when the network is
 * bad, which is exactly when the automatic poll is failing quietly.
 *
 * So this refreshes DATA, never the page. Reloading would throw away a warm app
 * and, on the signal that prompted the pull in the first place, replace it with
 * a blank screen for several seconds — the opposite of reassuring. Nothing is
 * at risk either way, since unsent scores live in the outbox, but there is no
 * reason to pay for a reload to answer a question the sync loop can answer.
 */

import { useEffect, useRef, useState } from 'react'
import { flush, pull, useStore } from './store'
import { applyUpdate, checkForUpdate } from './version'

/** How far to drag before letting go actually does something. */
const THRESHOLD = 70
/** Past this the indicator stops following, so it cannot be dragged to Cornwall. */
const MAX = 110

type Phase = 'idle' | 'pulling' | 'refreshing' | 'done'

export function PullToRefresh() {
  const [distance, setDistance] = useState(0)
  const [phase, setPhase] = useState<Phase>('idle')
  /*
   * Whether the server is serving a newer build than this page loaded.
   *
   * The pull still never reloads — that rationale stands — but "I dragged
   * down and the app didn't update" turned out to be a real expectation, so
   * the pull now at least ANSWERS it: each refresh also asks the server which
   * bundle it is serving, and a newer one leaves a bar offering the restart
   * instead of silently staying on the old build until the next cold launch.
   */
  const [stale, setStale] = useState(false)
  const online = useStore((s) => s.online && s.reachable)

  // Held in a ref rather than state: these change on every touchmove, and
  // re-rendering the whole app sixty times a second to track a finger would
  // make the drag stutter on the phones this is for.
  const start = useRef<{ x: number; y: number } | null>(null)
  const active = useRef(false)
  const phaseRef = useRef<Phase>('idle')
  const distanceRef = useRef(0)
  phaseRef.current = phase
  // The release handler needs the live distance but must not re-subscribe on
  // every pixel, so it reads it from here rather than from its closure.
  distanceRef.current = distance

  useEffect(() => {
    const onStart = (e: TouchEvent) => {
      // Only from a genuine top-of-page. scrollY goes negative during iOS
      // rubber-band, so this is <= rather than ===.
      if (window.scrollY > 0 || phaseRef.current === 'refreshing') return
      const t = e.touches[0]
      if (!t) return
      start.current = { x: t.clientX, y: t.clientY }
      active.current = false
    }

    const onMove = (e: TouchEvent) => {
      const s = start.current
      const t = e.touches[0]
      if (!s || !t) return
      const dy = t.clientY - s.y
      const dx = t.clientX - s.x

      /*
       * Claim the gesture only once it is unmistakably a downward pull.
       *
       * The scoring grid scrolls sideways and the whole app scrolls up, and
       * stealing either would be a far worse bug than not having this feature.
       * So the drag must be downward, from the top, and more vertical than
       * horizontal before a single pixel of it is consumed.
       */
      if (!active.current) {
        if (dy <= 0 || Math.abs(dy) < Math.abs(dx) * 1.5 || window.scrollY > 0) {
          if (dy < 0 || Math.abs(dx) > Math.abs(dy)) start.current = null
          return
        }
        active.current = true
        setPhase('pulling')
      }

      // Resistance, so the sheet feels attached to something.
      const pulled = Math.min(MAX, dy / 2)
      setDistance(pulled)
      // Safe because the listener is registered non-passive: without this the
      // page rubber-bands underneath the indicator and the two fight.
      if (e.cancelable) e.preventDefault()
    }

    const onEnd = () => {
      const wasActive = active.current
      const pulled = distanceRef.current
      start.current = null
      active.current = false

      if (!wasActive) return
      if (pulled < THRESHOLD) {
        setDistance(0)
        setPhase('idle')
        return
      }

      setPhase('refreshing')
      setDistance(56)
      // Push first, then read: sending what this phone is holding before asking
      // for everyone else's is what makes the refresh feel like it did
      // something when the thing you were waiting on was your own score.
      void flush()
        .then(pull)
        .then(checkForUpdate)
        .then((u) => setStale(u === 'stale'))
        .catch(() => {})
        .finally(() => {
          setPhase('done')
          setDistance(0)
          window.setTimeout(() => setPhase('idle'), 1400)
        })
    }

    window.addEventListener('touchstart', onStart, { passive: true })
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onEnd, { passive: true })
    window.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
      window.removeEventListener('touchcancel', onEnd)
    }
  }, [])

  if (phase === 'idle') {
    if (!stale) return null
    // The offer outlives the gesture: it sits there until taken or waved off.
    return (
      <div className="bg-signal text-paper flex items-stretch">
        <button
          type="button"
          onClick={() => void applyUpdate()}
          className="bc-tap flex-1 text-left px-4 py-2 bc-label"
        >
          Update ready — tap to restart on the new build
        </button>
        <button
          type="button"
          onClick={() => setStale(false)}
          className="bc-tap px-4 bc-label opacity-80"
        >
          Later
        </button>
      </div>
    )
  }

  const armed = distance >= THRESHOLD
  const label =
    phase === 'refreshing'
      ? online
        ? 'Refreshing…'
        : 'Still no signal'
      : phase === 'done'
        ? online
          ? stale
            ? 'New build found'
            : 'Up to date'
          : 'Still no signal'
        : armed
          ? 'Release to refresh'
          : 'Pull to refresh'

  return (
    <div
      className="overflow-hidden bg-wash text-mute flex items-end justify-center"
      style={{
        height: phase === 'done' ? 26 : distance,
        // No transition while a finger is on the glass — it has to track the
        // drag exactly — but easing on the way back makes the release land.
        transition: phase === 'pulling' ? 'none' : 'height 220ms ease-out',
      }}
    >
      <span className="bc-label text-[12px] pb-1.5 whitespace-nowrap">{label}</span>
    </div>
  )
}
